import { GridSortItem } from "@mui/x-data-grid";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useRef } from "react";
import { REFERRING_AGENCY_QUERY_KEY } from "@/constants/referring-agency-constant";
import { ReferringAgency, ReferringAgencyControllerApi } from "@/services";
import { ApiEnvelope, Identified } from "@/types/sdk-types";
import {
  DGT_Pagination,
  asPaginatedContent,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
  useMutationWithLoading,
} from "@/utils/utils";

interface UseReferringAgencyListProps {
  pagination: DGT_Pagination;
  gridSorting?: GridSortItem[];
  searchKey?: string;
  isArchived: boolean;
  isActive?: boolean;
}

interface ArchiveReferringAgencyParams {
  referringAgencyId: string;
  isArchived: boolean;
}

const getReferringAgencyApi = () =>
  new ReferringAgencyControllerApi(getConfigObject());

const useInvalidateReferringAgencies = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all(
      Object.values(REFERRING_AGENCY_QUERY_KEY).map((key) =>
        queryClient.invalidateQueries({ queryKey: [key] })
      )
    );
};

export const useReferringAgencyList = ({
  pagination,
  gridSorting,
  searchKey,
  isArchived,
  isActive,
}: UseReferringAgencyListProps): useBaseContent<
  Identified<ReferringAgency>
> => {
  const prevPage = useRef<number>(pagination.page);
  const pageIndex = getPageIndexWhileSearch({
    pagination,
    searchKey,
    prevPage,
  });
  const pageSize = getPageSize(searchKey, pagination);
  const sortDirection = gridSorting?.[0]?.sort ?? undefined;
  const sortBy = sortDirection ? gridSorting?.[0]?.field : undefined;
  const tenantId = getXtenantId();

  return useQuery({
    queryKey: [
      REFERRING_AGENCY_QUERY_KEY.LIST,
      tenantId,
      pageIndex,
      pageSize,
      sortBy,
      sortDirection,
      isArchived,
      isActive,
      searchKey,
    ],
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      getReferringAgencyApi()
        .getAllReferringAgencies(
          pageIndex,
          pageSize,
          sortBy,
          sortDirection,
          isArchived,
          isActive,
          searchKey || undefined,
          tenantId
        )
        .then((res) =>
          asPaginatedContent<Identified<ReferringAgency>>(res?.data)
        ),
  });
};

export const useReferringAgencyById = (referringAgencyId?: string) => {
  const tenantId = getXtenantId();

  return useQuery({
    queryKey: [REFERRING_AGENCY_QUERY_KEY.DETAIL, tenantId, referringAgencyId],
    enabled: !!referringAgencyId,
    refetchOnWindowFocus: false,
    queryFn: async () =>
      getReferringAgencyApi()
        .getReferringAgencyById(referringAgencyId ?? "", tenantId)
        .then(
          (res) => (res?.data as ApiEnvelope<Identified<ReferringAgency>>)?.data
        ),
  });
};

export const useCreateReferringAgency = () => {
  const invalidate = useInvalidateReferringAgencies();
  return useMutationWithLoading(
    async (agency: ReferringAgency) =>
      getReferringAgencyApi()
        .createReferringAgency(agency, getXtenantId())
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};

export const useUpdateReferringAgency = () => {
  const invalidate = useInvalidateReferringAgencies();
  return useMutationWithLoading(
    async (agency: ReferringAgency) =>
      getReferringAgencyApi()
        .updateReferringAgency(agency, getXtenantId())
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};

export const useArchiveReferringAgency = () => {
  const invalidate = useInvalidateReferringAgencies();
  return useMutationWithLoading(
    async ({ referringAgencyId, isArchived }: ArchiveReferringAgencyParams) =>
      getReferringAgencyApi()
        .updateReferringAgencyArchiveStatus(
          referringAgencyId,
          isArchived,
          getXtenantId()
        )
        .then((res) => res?.data),
    { onSuccess: invalidate }
  );
};
